import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid2,
  IconButton,
  Link,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { Leads } from "../../../../../models/Leads/leads";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";

interface LeadDetailsPopupProps {
  open: boolean;
  lead: Leads | null;
  onClose: () => void;
}

const LeadDetailsPopup: React.FC<LeadDetailsPopupProps> = ({
  open,
  lead,
  onClose,
}) => {
  const theme = useTheme();

  if (!lead) return null;

  const summaryCards = [
    { label: "ID", value: lead.id },
    { label: "Kylas ID", value: lead.kylas_lead_id || "-" },
    { label: "Source", value: lead.source || "-" },
    { label: "Lead Source", value: lead.lead_source || "-" },
  ];

  const contactInfo = [
    { label: "Name", value: lead.name || "-" },
    { label: "Email", value: lead.email_id || "-" },
    { label: "Mobile", value: lead.mobile || "-" },
  ];

  const companyInfo = [
    { label: "Company Name", value: lead.company_name || "-" },
    { label: "Company Size", value: lead.company_size || "-" },
    { label: "Department", value: lead.department_name || "-" },
  ];

  const eventDetails = [
    { label: "Event Type", value: lead.event_type || "-" },
    { label: "Attendees (PAX)", value: lead.attendees_pax ?? "-" },
    { label: "No. of Rooms", value: lead.no_of_rooms ?? "-" },
    { label: "From City", value: lead.from_city || "-" },
    { label: "To City", value: lead.to_city || "-" },
    { label: "Travel Time", value: lead.travel_time || "-" },
  ];

  const trackingInfo = [
    { label: "Referral ID", value: lead.referral_id || "-" },
    { label: "UTM Source", value: lead.utm_source || "-" },
    { label: "UTM Medium", value: lead.utm_medium || "-" },
    { label: "UTM Campaign ID", value: lead.utm_campaignid || "-" },
    { label: "UTM Keyword", value: lead.utm_keyword || "-" },
    { label: "UTM Term", value: lead.utm_term || "-" },
    { label: "UTM Device", value: lead.utm_device || "-" },
    {
      label: "UTM Location",
      value: lead.utm_loc_physical_ms || "-",
    },
    { label: "UTM Match Type", value: lead.utm_matchtype || "-" },
  ];

  const technicalInfo = [
    { label: "Browser", value: lead.browser || "-" },
    { label: "Timezone", value: lead.timezone || "-" },
    { label: "Device Type", value: lead.submission_device_type || "-" },
  ];

  const urlInfo = [
    {
      label: "Landing Page URL",
      value: lead.landing_page_url,
      isLink: true,
    },
    {
      label: "Form Submission Page URL",
      value: lead.form_submission_page_url,
      isLink: true,
    },
    {
      label: "Referrer URL",
      value: lead.referrer_url,
      isLink: true,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            boxShadow:
              "0px 30px 60px rgba(15, 23, 42, 0.18), 0px 12px 24px rgba(15, 23, 42, 0.12)",
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(180deg, ${
              theme.palette.customColors?.white || "#FFFFFF"
            } 0%, ${
              theme.palette.customColors?.greyBackground || "#F6F7FB"
            } 100%)`,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(120deg, ${
            theme.palette.customColors?.lightPurple || "#E0E7FF"
          } 0%, ${theme.palette.customColors?.white || "#FFFFFF"} 100%)`,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
          <CustomTypography variant="heading4" fontWeight={700}>
            Lead Details — #{lead.id}
          </CustomTypography>
          <CustomTypography variant="body2" color="text.secondary">
            Complete information and tracking data for this lead
          </CustomTypography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          px: 3,
          py: 2,
        }}
      >
        <Grid2 container spacing={1} sx={{ mb: 2 }}>
          {summaryCards.map((card) => (
            <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
              <Box
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.3,
                }}
              >
                <CustomTypography variant="body2" color="text.secondary">
                  {card.label}
                </CustomTypography>
                <CustomTypography variant="heading6" sx={{ mt: 0.5 }}>
                  {card.value || "-"}
                </CustomTypography>
              </Box>
            </Grid2>
          ))}
        </Grid2>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <CustomTypography variant="heading6" style={{ marginBottom: 10 }}>
            Contact Information
          </CustomTypography>
          <Grid2 container spacing={1}>
            {contactInfo.map((row) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={row.label}>
                <Box
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  <CustomTypography
                    variant="body2"
                    color={theme.palette.text.secondary}
                  >
                    {row.label}
                  </CustomTypography>
                  <CustomTypography variant="heading7" sx={{ mt: 0.5 }}>
                    {row.value || "-"}
                  </CustomTypography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <CustomTypography variant="heading6" style={{ marginBottom: 10 }}>
            Company Information
          </CustomTypography>
          <Grid2 container spacing={1}>
            {companyInfo.map((row) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={row.label}>
                <Box
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  <CustomTypography
                    variant="body2"
                    color={theme.palette.text.secondary}
                  >
                    {row.label}
                  </CustomTypography>
                  <CustomTypography variant="heading7" sx={{ mt: 0.5 }}>
                    {row.value || "-"}
                  </CustomTypography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <CustomTypography variant="heading6" style={{ marginBottom: 10 }}>
            Event Details
          </CustomTypography>
          <Grid2 container spacing={1}>
            {eventDetails.map((row) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={row.label}>
                <Box
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  <CustomTypography
                    variant="body2"
                    color={theme.palette.text.secondary}
                  >
                    {row.label}
                  </CustomTypography>
                  <CustomTypography variant="heading7" sx={{ mt: 0.5 }}>
                    {row.value ?? "-"}
                  </CustomTypography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <CustomTypography variant="heading6" style={{ marginBottom: 10 }}>
            Tracking Information
          </CustomTypography>
          <Grid2 container spacing={1}>
            {trackingInfo.map((row) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={row.label}>
                <Box
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px dashed ${theme.palette.divider}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  <CustomTypography variant="body2" color="text.secondary">
                    {row.label}
                  </CustomTypography>
                  <CustomTypography variant="body1" fontWeight={600}>
                    {row.value}
                  </CustomTypography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <CustomTypography variant="heading6" style={{ marginBottom: 10 }}>
            Technical Information
          </CustomTypography>
          <Grid2 container spacing={1}>
            {technicalInfo.map((row) => (
              <Grid2 size={{ xs: 12, sm: 6 }} key={row.label}>
                <Box
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  <CustomTypography
                    variant="body2"
                    color={theme.palette.text.secondary}
                  >
                    {row.label}
                  </CustomTypography>
                  <CustomTypography variant="heading7" sx={{ mt: 0.5 }}>
                    {row.value || "-"}
                  </CustomTypography>
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: 2 }}>
          <CustomTypography variant="heading6" style={{ marginBottom: 10 }}>
            URLs
          </CustomTypography>
          <Grid2 container spacing={1}>
            {urlInfo.map((row) => (
              <Grid2 size={{ xs: 12 }} key={row.label}>
                <Box
                  sx={{
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  <CustomTypography
                    variant="body2"
                    color={theme.palette.text.secondary}
                  >
                    {row.label}
                  </CustomTypography>
                  {row.value && row.isLink ? (
                    <Link
                      href={row.value}
                      target="_blank"
                      sx={{
                        mt: 0.5,
                        textDecoration: "underline",
                        color:
                          theme.palette.customColors?.text ||
                          theme.palette.primary.main,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {row.value}
                    </Link>
                  ) : (
                    <CustomTypography variant="heading7" sx={{ mt: 0.5 }}>
                      {row.value || "-"}
                    </CustomTypography>
                  )}
                </Box>
              </Grid2>
            ))}
          </Grid2>
        </Box>
        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1 }}>
          <CustomTypography variant="heading6">Timeline</CustomTypography>
          <Grid2 container spacing={1}>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Box
                sx={{
                  py: 1,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px dashed ${theme.palette.divider}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.3,
                }}
              >
                <CustomTypography variant="body2" color="text.secondary">
                  Created Date
                </CustomTypography>
                <CustomTypography variant="body1" fontWeight={600}>
                  {formatDate(lead.created_date)}
                </CustomTypography>
              </Box>
            </Grid2>
          </Grid2>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <AppButton variant="grayscale" label="Close" onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
};

export default LeadDetailsPopup;
